<template>
  <span>
    <!-- Activator: the eye icon used on board cards / tables. -->
    <v-icon v-bind="activatorBind" aria-label="View ticket details" style="cursor: pointer">
      mdi-eye
    </v-icon>

    <v-dialog v-model="dialog" max-width="680" scrollable>
      <v-card class="vt-card">
        <!-- Header -->
        <div class="vt-head d-flex align-start justify-space-between pa-4">
          <div class="text-left" style="min-width:0">
            <div class="vt-ref">{{ details.ticket_id || 'Ticket' }}</div>
            <div class="vt-title">{{ details.name || details.title || 'Untitled ticket' }}</div>
          </div>
          <v-btn icon="mdi-close" variant="text" size="small" aria-label="Close" @click="dialog = false"></v-btn>
        </div>

        <v-divider></v-divider>

        <v-card-text class="text-left">
          <!-- ============ Read-only view ============ -->
          <template v-if="!editing">
            <!-- Status + priority chips -->
            <div class="d-flex flex-wrap align-center mb-4" style="gap:10px">
              <StatusChip v-if="details.status" :status="details.status" />
              <PriorityChip v-if="details.priority" :priority="details.priority" />
              <v-chip v-if="projectKey" size="small" color="secondary" variant="flat">{{ projectKey }}</v-chip>
              <v-chip v-if="sprintLabel" size="small" variant="tonal" prepend-icon="mdi-run-fast">{{ sprintLabel }}</v-chip>
            </div>

            <!-- Field grid -->
            <v-row dense>
              <v-col v-for="field in shownFields" :key="field.key" cols="12" sm="6">
                <div class="vt-label">
                  <v-icon size="13" class="vt-label-ic">{{ field.icon }}</v-icon>{{ field.label }}
                </div>
                <div class="vt-value">{{ field.value }}</div>
              </v-col>
            </v-row>

            <!-- Description -->
            <div v-if="description" class="mt-4">
              <div class="vt-label"><v-icon size="13" class="vt-label-ic">mdi-text-long</v-icon>Description</div>
              <div class="vt-desc">{{ description }}</div>
            </div>

            <EmptyState
              v-if="!hasAnyDetail"
              dense
              icon="mdi-ticket-outline"
              title="No details available"
              subtitle="This ticket has no additional information to show."
            />
          </template>

          <!-- ============ Edit form ============ -->
          <template v-else>
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model="edit.name" label="Title*" density="compact" prepend-inner-icon="mdi-format-title"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="edit.status" :items="statuses" label="Status*" density="compact" prepend-inner-icon="mdi-flag-outline"></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="edit.priority" :items="priorities" label="Priority*" density="compact" prepend-inner-icon="mdi-alert-circle-outline"></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="edit.assignees"
                  :items="employeeItems"
                  item-title="title"
                  item-value="value"
                  label="Assignees"
                  multiple
                  chips
                  closable-chips
                  density="compact"
                  prepend-inner-icon="mdi-account-multiple"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="edit.due_date" type="date" label="Due date" density="compact" prepend-inner-icon="mdi-calendar-clock" clearable></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="edit.description" label="Description" rows="3" density="compact" prepend-inner-icon="mdi-text-long"></v-textarea>
              </v-col>
            </v-row>
          </template>

          <!-- Linked test cases (QA cases referencing this ticket). -->
          <template v-if="canTransfer && !editing">
            <v-divider class="my-4"></v-divider>
            <div class="vt-label"><v-icon size="13" class="vt-label-ic">mdi-test-tube</v-icon>Linked test cases</div>
            <div v-if="casesLoading" class="py-3"><v-progress-circular indeterminate size="20"></v-progress-circular></div>
            <div v-else-if="linkedCases.length" class="mt-1">
              <div v-for="c in linkedCases" :key="c.id" class="vt-case d-flex align-center justify-space-between">
                <div style="min-width:0">
                  <div class="vt-value text-truncate">{{ c.title }}</div>
                  <div class="text-caption text-grey">
                    <span>{{ c.assignee_name || 'Unassigned' }}</span>
                    <span> · {{ c.last_run_at ? `last run ${formattedDateTime(c.last_run_at)}` : 'never run' }}</span>
                  </div>
                </div>
                <div class="d-flex align-center" style="gap:8px">
                  <v-chip size="x-small" variant="flat" :color="resultColor(c.latest_result)">{{ c.latest_result || 'Untested' }}</v-chip>
                  <PriorityChip v-if="c.priority" :priority="c.priority" />
                  <v-btn size="x-small" variant="text" icon="mdi-open-in-new" aria-label="Open test case" @click="goToTestCase(c)"></v-btn>
                </div>
              </div>
            </div>
            <div v-else class="text-caption text-grey py-1">No test cases linked to this ticket.</div>

            <!-- Activity / history timeline. -->
            <v-divider class="my-4"></v-divider>
            <div class="vt-label"><v-icon size="13" class="vt-label-ic">mdi-timeline-text-outline</v-icon>History</div>
            <div v-if="historyLoading" class="py-3"><v-progress-circular indeterminate size="20"></v-progress-circular></div>
            <v-timeline v-else-if="history.length" density="compact" side="end" class="mt-2">
              <v-timeline-item v-for="h in history" :key="h.id" :dot-color="historyColor(h)" size="x-small">
                <div class="d-flex justify-space-between" style="gap:12px">
                  <div style="min-width:0">
                    <div class="vt-value">{{ h.message }}</div>
                    <div class="text-caption text-grey">{{ h.updated_by }}</div>
                  </div>
                  <div class="text-caption text-grey text-no-wrap">{{ formattedDateTime(h.created_at) }}</div>
                </div>
              </v-timeline-item>
            </v-timeline>
            <div v-else class="text-caption text-grey py-1">No history yet.</div>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-4 py-3">
          <!-- Edit mode: save / cancel only. -->
          <template v-if="editing">
            <v-spacer></v-spacer>
            <v-btn variant="text" size="small" :disabled="saving" @click="cancelEdit">Cancel</v-btn>
            <v-btn color="primary" variant="flat" size="small" :loading="saving" :disabled="!edit.name" @click="saveEdit">Save changes</v-btn>
          </template>
          <template v-else>
            <!-- Inline delete confirmation keeps a destructive action one extra click away. -->
            <template v-if="canDelete && confirmDelete">
              <span class="text-caption text-error font-weight-medium mr-2">Delete this ticket?</span>
              <v-btn color="error" variant="flat" size="small" :loading="deleting" @click="removeTicket">Yes, delete</v-btn>
              <v-btn variant="text" size="small" :disabled="deleting" @click="confirmDelete = false">Cancel</v-btn>
            </template>
            <template v-else-if="canDelete">
              <v-btn color="error" variant="text" size="small" prepend-icon="mdi-delete-outline" @click="confirmDelete = true">Delete</v-btn>
            </template>
            <v-btn v-if="canEdit" variant="text" size="small" prepend-icon="mdi-pencil-outline" @click="startEdit">Edit</v-btn>
            <v-btn v-if="canTransfer" variant="text" size="small" prepend-icon="mdi-account-switch-outline" @click="openTransfer">Transfer</v-btn>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="dialog = false">Close</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <TransferDialog
      v-model="transferDialog"
      :item="details"
      :endpoint="constant.ticket_transfer"
      id-key="ticket_id"
      entity-label="ticket"
      :title="details.ticket_id || ''"
      :employees="employees"
      :current-name="assigneeLabel"
      :updated-by="currentUserLabel()"
      @transferred="onTransferred"
    ></TransferDialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </span>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import StatusChip from '@/components/ui/StatusChip.vue';
import PriorityChip from '@/components/ui/PriorityChip.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import Snackbar from './Snackbar.vue';
import TransferDialog from './TransferDialog.vue';

export default {
  name: 'ViewTicket',
  mixins: [apiMixin, formatMixin],
  components: { StatusChip, PriorityChip, EmptyState, Snackbar, TransferDialog },
  props: ['ticketData'],
  emits: ['deleted', 'transferred', 'updated'],
  data() {
    return {
      dialog: false,
      constant: constant,
      details: this.ticketData || {},
      user_data: null,
      confirmDelete: false,
      deleting: false,
      isshow: false,
      message: '',
      transferDialog: false,
      employees: [],
      history: [],
      historyLoading: false,
      linkedCases: [],
      casesLoading: false,
      // Edit mode.
      editing: false,
      saving: false,
      statuses: ['Todo', 'In Progress', 'On Hold', 'Cancelled', 'Done'],
      priorities: ['lowest', 'low', 'medium', 'high', 'critical'],
      edit: { name: '', description: '', status: 'Todo', priority: 'medium', assignees: [], due_date: null },
    };
  },
  computed: {
    // The bare <v-icon> activator needs the dialog's activator props to open
    // it on click; Vuetify wires this when the dialog has an activator slot,
    // but here we toggle manually so the icon works inside table/menu cells.
    activatorBind() {
      return { onClick: () => { this.dialog = true; } };
    },
    description() {
      const d = this.details || {};
      return d.description || d.details || d.body || '';
    },
    // Project / sprint can arrive either as nested objects (board, explorer)
    // or as flat strings; normalise both so we never dump raw JSON.
    projectKey() {
      const d = this.details || {};
      if (d.project && typeof d.project === 'object') return d.project.key || d.project.title || '';
      return d.project_key || '';
    },
    projectLabel() {
      const d = this.details || {};
      if (d.project_title) return d.project_title;
      if (d.project && typeof d.project === 'object') return d.project.title || d.project.key || '';
      if (typeof d.project === 'string') return d.project;
      return d.project_id ? `#${d.project_id}` : '';
    },
    sprintLabel() {
      const d = this.details || {};
      if (d.sprint_name) return d.sprint_name;
      if (d.sprint && typeof d.sprint === 'object') return d.sprint.name || '';
      if (typeof d.sprint === 'string') return d.sprint;
      return '';
    },
    assigneeLabel() {
      const d = this.details || {};
      if (Array.isArray(d.assignees_details) && d.assignees_details.length) {
        return d.assignees_details.map(a => a.fullname).join(', ');
      }
      return d.assignee || d.assigned_to || d.assignee_name || '';
    },
    canDelete() {
      // Delete is wired for project tickets (identified by ticket_id).
      return !!(this.details && this.details.ticket_id);
    },
    canTransfer() {
      // Transfer is wired for project/sprint tickets (identified by ticket_id).
      return !!(this.details && this.details.ticket_id);
    },
    canEdit() {
      // Edit is wired for project/sprint tickets (identified by ticket_id).
      return !!(this.details && this.details.ticket_id);
    },
    employeeItems() {
      return (this.employees || []).map((e) => ({ title: e.fullname, value: e.id }));
    },
    // Render only the descriptive fields that are actually present, so the
    // panel adapts to sprint / IT / QA tickets without empty rows.
    shownFields() {
      const d = this.details || {};
      const out = [];
      const push = (label, value, icon) => {
        if (value !== null && value !== undefined && value !== '') out.push({ key: label, label, value, icon });
      };
      push('Assignee', this.assigneeLabel, 'mdi-account-outline');
      push('Created by', d.created_by || d.reporter || d.requester, 'mdi-account-plus-outline');
      push('Updated by', d.updated_by, 'mdi-account-edit-outline');
      push('Project', this.projectLabel, 'mdi-folder-outline');
      push('Sprint', this.sprintLabel, 'mdi-run-fast');
      push('Type', d.type, 'mdi-shape-outline');
      push('Due date', d.due_date ? this.formattedDate(d.due_date) : '', 'mdi-calendar-clock');
      push('Created', d.created_at ? this.formattedDateTime(d.created_at) : '', 'mdi-clock-plus-outline');
      push('Updated', d.updated_at ? this.formattedDateTime(d.updated_at) : '', 'mdi-clock-edit-outline');
      return out;
    },
    hasAnyDetail() {
      return this.shownFields.length > 0 || !!this.description || !!this.details.status || !!this.details.priority;
    },
  },
  watch: {
    // Keep the panel in sync if the parent swaps the ticket prop.
    ticketData(val) { this.details = val || {}; },
    dialog(open) {
      if (!open) { this.confirmDelete = false; this.editing = false; return; }
      // Load history + linked test cases when opening a project/sprint ticket.
      if (this.details && this.details.ticket_id) {
        this.loadHistory();
        this.loadLinkedCases();
      }
    },
  },
  methods: {
    notify(msg) {
      this.message = msg;
      this.isshow = true;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    resultColor(result) {
      switch (result) {
        case 'Pass': return 'success';
        case 'Fail': return 'error';
        case 'Blocked': return 'warning';
        case 'Skipped': return 'grey';
        default: return 'grey-lighten-1';
      }
    },
    // Jump to the QA page and open this test case. Uses a window event so the
    // deeply-nested dialog doesn't need prop wiring through every parent.
    goToTestCase(c) {
      if (!c || !c.id) return;
      localStorage.setItem('qa_open_case_id', String(c.id));
      window.dispatchEvent(new CustomEvent('int:navigate', { detail: { nav: 'QA / Test Cases' } }));
      this.dialog = false;
    },
    historyColor(h) {
      const msg = (h.message || '').toLowerCase();
      if (h.previous_status == null) return 'primary';
      if (msg.includes('transfer')) return 'warning';
      return 'info';
    },
    loadHistory() {
      this.historyLoading = true;
      this.history = [];
      axios.post(utils._api(this.constant.list_history), { ticket_id: this.details.ticket_id }, this.authHeaders())
        .then((response) => { this.history = response.data.data || []; })
        .catch((error) => { console.error('Error fetching ticket history:', error); })
        .finally(() => { this.historyLoading = false; });
    },
    loadLinkedCases() {
      this.casesLoading = true;
      this.linkedCases = [];
      axios.get(utils._api(this.constant.qa_list), { ...this.authHeaders(), params: { ticket_id: this.details.ticket_id } })
        .then((response) => { this.linkedCases = response.data.data || []; })
        .catch((error) => { console.error('Error fetching linked test cases:', error); })
        .finally(() => { this.casesLoading = false; });
    },
    openTransfer() {
      // Load the member list lazily the first time a transfer is requested.
      if (!this.employees.length) {
        axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
          .then((response) => { this.employees = response.data.data || []; })
          .catch((error) => { console.error('Error fetching users:', error); });
      }
      this.transferDialog = true;
    },
    onTransferred(item, errorMsg) {
      if (errorMsg) { this.notify(errorMsg); return; }
      this.notify('Ticket transferred');
      this.dialog = false;
      this.$emit('transferred', this.details.ticket_id);
    },
    startEdit() {
      const d = this.details || {};
      // Prefill from the current ticket; assignees come from assignees_details
      // (array of {id}) or the comma-separated assignees string.
      let assignees = [];
      if (Array.isArray(d.assignees_details) && d.assignees_details.length) {
        assignees = d.assignees_details.map((a) => a.id);
      } else if (typeof d.assignees === 'string' && d.assignees) {
        assignees = d.assignees.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
      }
      this.edit = {
        name: d.name || d.title || '',
        description: d.description || '',
        status: d.status || 'Todo',
        priority: d.priority || 'medium',
        assignees,
        due_date: d.due_date ? String(d.due_date).slice(0, 10) : null,
      };
      // Lazy-load the member list for the assignee picker.
      if (!this.employees.length) {
        axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
          .then((response) => { this.employees = response.data.data || []; })
          .catch((error) => { console.error('Error fetching users:', error); });
      }
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
    },
    saveEdit() {
      if (!this.details.ticket_id || !this.edit.name) return;
      this.saving = true;
      const payload = {
        ticket_id: this.details.ticket_id,
        name: this.edit.name,
        description: this.edit.description || null,
        status: this.edit.status,
        priority: this.edit.priority,
        assignees: this.edit.assignees || [],
        due_date: this.edit.due_date || null,
        updated_by: this.currentUserLabel(),
      };
      axios.post(utils._api(this.constant.update_ticket), payload, this.authHeaders())
        .then((response) => {
          const updated = (response.data && response.data.data) || {};
          // Merge the saved fields back into the local view so it reflects
          // immediately, and refresh the assignee labels.
          this.details = {
            ...this.details,
            ...updated,
            assignees_details: this.edit.assignees.map((id) => {
              const emp = this.employees.find((e) => e.id === id);
              return { id, fullname: emp ? emp.fullname : String(id) };
            }),
          };
          this.notify('Ticket updated');
          this.editing = false;
          if (this.details.ticket_id) { this.loadHistory(); }
          this.$emit('updated', this.details.ticket_id);
        })
        .catch((error) => {
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Failed to update ticket';
          console.error('Error updating ticket:', error);
          this.notify(msg);
        })
        .finally(() => { this.saving = false; });
    },
    removeTicket() {
      if (!this.details.ticket_id) return;
      this.deleting = true;
      axios.post(utils._api(this.constant.delete_ticket), {
        ticket_id: this.details.ticket_id,
        updated_by: this.currentUserLabel(),
      }, this.authHeaders())
        .then(() => {
          this.notify('Ticket deleted');
          this.confirmDelete = false;
          this.dialog = false;
          this.$emit('deleted', this.details.ticket_id);
        })
        .catch((error) => {
          console.error('Error deleting ticket:', error);
          this.notify('Failed to delete ticket');
        })
        .finally(() => { this.deleting = false; });
    },
  },
  created() {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = null; }
    }
  },
};
</script>

<style scoped>
.vt-ref {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--int-text-faint);
}
.vt-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--int-text-strong);
  line-height: 1.2;
}
.vt-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--int-text-faint);
  margin-top: 10px;
  display: flex;
  align-items: center;
}
.vt-label-ic {
  margin-right: 4px;
  color: var(--int-text-faint);
}
.vt-value {
  font-size: 0.9rem;
  color: var(--int-text);
  font-weight: 500;
}
.vt-case {
  padding: 6px 0;
  border-bottom: 1px solid var(--int-border, #eee);
  gap: 10px;
}
.vt-case:last-child {
  border-bottom: none;
}
.vt-desc {
  font-size: 0.9rem;
  color: var(--int-text);
  white-space: pre-wrap;
  line-height: 1.5;
  background: var(--int-surface-muted);
  border-radius: var(--int-radius-sm);
  padding: 10px 12px;
  margin-top: 4px;
}
</style>
